import { ISegmentList, useSegmentFilterCascaded, useStatesApi } from "@repo/baseCloud";
import {
	updatePropertyFn,
	updateValuesWrapper,
	useMultiplePropUpdates,
} from "@repo/dynamicEditor/src/helpers/updatePropertiesHooks";
import { PushProcessingNotification } from "@repo/helpers";
import { ActivableFilterItem, FilterItem, KeyedArray, keyPair, RepTypeEnum } from "@repo/models";
import { Col } from "@repo/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";

import { useOnboardingSessionsApi } from "../../api/onboardingSessionsApi";
import * as Queries from "../../api/queries";
import { useFilterItemQuery } from "../../api/queries";
import { useRepApi, useRepMachineApi, useRepQueries } from "../../api/repApi";
import { useDuplicateCheck } from "../../components/duplicateRepModal";
import { EditableItemDynamicTab } from "../../components/dynamicEditor/dynamicTab/editableItem";
import { ITemplateData } from "../../components/dynamicEditor/templateDataHook";
import { SingleOnboardingCompletion } from "../../components/onboardingCompletion";
import { useRepContext } from "../../contexts/editableItemContext";
import { useSegmentCascade, useSettingsQuery } from "../../helpers/queryHooks";
import { useUpdateRep } from "../../helpers/urlHooks";
import { Rep } from "../../models";
import { tabs } from "../../string";

function PersonalInfo() {
	const repContext = useRepContext();
	const queryClient = useQueryClient();
	const {
		checkIfLoginExists,
		updateOnboarding,
		saveRepPhoto,
		loadRep,
		saveRep,
		getAssignedMachine,
		saveMachineAssignment,
	} = useRepApi();
	const { checkForDuplicates } = useDuplicateCheck();
	const { rep, savedRep, repLoaded } = repContext;
	const allLoaded = useMemo(() => repLoaded, [repLoaded]);
	const updateRep = useUpdateRep();

	const [settings] = useSettingsQuery();
	const activeSegmentLists = useRepSegmentFilter();
	const activePersonalInfoLists = usePersonalInfoLists();
	const machineInfo = useMachineInfo();
	const { getPossibleStates } = useOnboardingSessionsApi();
	const { getCityByZip } = useStatesApi();
	const { disabledFeatures } = useFeatureInfo();
	const templateData = useMemo(() => {
		return {
			activeSegmentLists,
			activePersonalInfoLists,
			activeMachineInfoLists: {
				machines: machineInfo.machines,
				machinesLoaded: machineInfo.machinesLoaded,
			},
			disabledFeatures,
		};
	}, [activePersonalInfoLists, activeSegmentLists, machineInfo]);

	useEffect(() => {
		const updateRep = { machineId: machineInfo.assigned?.machineId };

		repContext.updateItem(updateRep);
		// The loaded rep has no machineId, so keep it in the saved rep as well: otherwise Cancel
		// (overwriteItem(savedItem)) drops the assignment and the next Save unassigns the machine
		repContext.updateSavedItem(updateRep);
	}, [machineInfo]);

	useEffect(() => {
		if (!settings.defaultValues) return;
		if (!rep) return;

		if (rep.repId === 0 || rep.repId == null) {
			const defaultValues = {} as Partial<Rep>;
			for (const defaultProp of Object.keys(settings.defaultValues)) {
				defaultValues[defaultProp] = getValue(defaultProp, settings.defaultValues[defaultProp]);
			}
			repContext.updateItem(defaultValues);
			repContext.updateSavedItem(defaultValues);
		}
		function getValue(name: string, val: string) {
			switch (name) {
				case "eclEnabled":
				case "dlScanEnabled":
				case "dlScanRequired":
					return val === "true";
				default:
					if (name.endsWith("Id")) return parseInt(val);
					return val;
			}
		}
	}, [settings, rep.repId, rep.branchId]);

	const save = useCallback(async () => {
		const notify = PushProcessingNotification("Saving Rep...");
		try {
			await saveMachineAssignment(rep.login, rep.machineId);

			if (rep.login !== savedRep.login) {
				const loginAlreadyExists = await checkIfLoginExists(rep);
				if (loginAlreadyExists) {
					notify.error("Login already exists!");
					return;
				}
			}
			if (!rep.repId) {
				const doSave = await checkForDuplicates(notify);
				if (!doSave) return;
			}
			const repId = await saveRep(rep);
			if (rep.photo) {
				notify.processing("Saving Rep Photo...");
				await saveRepPhoto(repId, rep.photo);
			}

			await updateOnboarding(repId);

			await queryClient.invalidateQueries({ queryKey: [Queries.SETTINGS_REP] });
			await queryClient.invalidateQueries({
				queryKey: [...Queries.REP_MACHINES_ACTIVE, rep.branchId],
			});
			await queryClient.invalidateQueries({
				queryKey: [...Queries.REP_MACHINES_ASSIGNED, rep.login],
			});

			await queryClient.invalidateQueries({
				predicate: (q) => q.queryKey.includes(Queries.GROUP.REP),
			});

			const newRep = await loadRep(repId);
			const machine = await getAssignedMachine(newRep.login);
			newRep.machineId = machine?.machineId;

			repContext.updateItem(newRep);

			if (!rep.repId) {
				const possibleStates = await getPossibleStates(repId);

				const shouldRedirectToOnboardingSessions = settings.multiStateEnabled && possibleStates.length > 1;

				const targetTab = shouldRedirectToOnboardingSessions ? tabs.onboardingStates : tabs.summary;

				await updateRep(newRep.repId, targetTab);
			}

			notify.success("Saved Rep");

			return newRep;
		} catch (ex) {
			notify.error("Failed to save");
			throw ex;
		}
	}, [rep, savedRep, queryClient, checkForDuplicates]);

	async function updateValueInternal(
		id: string,
		value: any,
		currentState: IPersonalInfoData,
		parentComponent?: string,
	) {
		const { newState } = await updateValues(id, value, currentState, parentComponent);
		return (newState ?? currentState) as IPersonalInfoData;
	}

	const cascadeSegment = useSegmentCascade(updateValueInternal, "rep");
	const customUpdates = useMultiplePropUpdates(
		cascadeSegment,
		applyBusinessLogic,
	) as updatePropertyFn<IPersonalInfoData>;
	const updateValues = useMemo(() => updateValuesWrapper<IPersonalInfoData>(customUpdates), [customUpdates]);

	async function applyBusinessLogic(id: string, value: any, newState: IPersonalInfoData) {
		async function update(name: string, value: any) {
			newState = await updateValueInternal(name, value, newState);
		}
		const clear = async (name: string) => await update(name, null);
		// Custom logic
		switch (id) {
			case "rep.dateOfBirth":
				if (value && settings.autogeneratePin) {
					const date = new Date(value);
					const newPin = pad(date.getMonth() + 1) + pad(date.getDate());
					await update("rep.pin", newPin);
				}
				break;
			case "rep.pucOfTexasRegisteredBroker":
				if (!value) await clear("rep.brokerRegistrationNumber");
				break;
			case "rep.positionId":
				// Temporary while both versions of property are there
				await update("rep.repTypeId", value);
				await update("rep.isManager", value === RepTypeEnum.Manager);
				break;
			case "rep.isManager":
				if (value) {
					await clear("rep.managerId");
					await clear("rep.managerName");
				}
				break;
			case "rep.isTrainer":
				if (value) {
					await clear("rep.trainerId");
					await clear("rep.trainerName");
				}
				break;
			case "rep.mappedAttributes.zip": {
				const postalCode = await getCityByZip(value);
				await update("rep.mappedAttributes.city", postalCode?.city ?? null);
				await update("rep.mappedAttributes.state", postalCode?.stateName ?? null);
				break;
			}
		}
		if (id.startsWith("rep.mappedAttributes") && id.endsWith("Id")) await update(id.slice(0, id.length - 2), value);
		return newState as IPersonalInfoData;
	}

	return (
		<>
			<Col md={12} tag="section" id="onboarding">
				<SingleOnboardingCompletion req={"photo"} depthToDisplay={0} />
				<SingleOnboardingCompletion req={"phoneNumber"} depthToDisplay={0} />
			</Col>
			<EditableItemDynamicTab
				itemContext={repContext}
				pageName={tabs.personalInfo}
				otherTemplateData={templateData}
				itemProp="rep"
				updateValues={updateValues}
				save={save}
				loaded={allLoaded}
			/>
		</>
	);
}

function pad(num: number) {
	return ("0" + num.toString()).slice(-2);
}

export interface IPersonalInfoData extends ITemplateData {
	activeSegmentLists?: IRepSegmentList;
	activePersonalInfoLists?: IPersonalInfoList;
}
interface IRepSegmentList extends ISegmentList<keyPair[]> {}

export default PersonalInfo;

/**
 * Checks selected items, and filters other dropdown options based on selection (i.e. filters marketers based on channel)
 * Does this by checking which branches are associated with selected items
 */
function useRepSegmentFilter() {
	const { rep } = useRepContext();
	return useSegmentFilterCascaded<keyPair[]>(rep, (i: KeyedArray<FilterItem>) =>
		i.map((c) => {
			return { id: c.id, value: c.name ?? c.value };
		}),
	) as IRepSegmentList;
}

function usePersonalInfoLists() {
	const { repLoaded } = useRepContext();
	const { getManagers, getLanguages, getRepTypes, getTrainers } = useRepApi();
	const [managers, managersLoaded] = useManagerQuery(Queries.MANAGERS, getManagers);
	const [languages, languagesLoaded] = useFilterItemQuery([Queries.LANGUAGES], getLanguages);
	const [repTypes, repTypesLoaded] = useFilterItemQuery([Queries.REPTYPES], getRepTypes);
	const [trainers, trainersLoaded] = useManagerQuery(Queries.TRAINERS, getTrainers);

	return useMemo(() => {
		return {
			managers: getItems(managers, managersLoaded),
			languages: getItems(languages, languagesLoaded),
			repTypes: getItems(repTypes, repTypesLoaded),
			trainers: getItems(trainers, trainersLoaded),
			shirtSizes: ["S", "M", "L", "XL", "XXL"].map((v) => ({
				id: v,
				value: v,
			})),
		} as IPersonalInfoList;
	}, [repLoaded, managers, managersLoaded, languagesLoaded, repTypesLoaded, trainers, trainersLoaded]);

	function getItems(allItems: KeyedArray<FilterItem>, loaded: boolean, forceInclude?: FilterItem) {
		if (!loaded) return null as keyPair[];
		if (forceInclude) allItems.set(forceInclude);
		return allItems.map((c) => {
			return { id: c.id, value: c.name ?? c.value } as keyPair;
		});
	}
}

function useMachineInfo() {
	const { useActiveMachinesQuery, useAssignedMachineQuery } = useRepMachineApi();

	const machinesQuery = useActiveMachinesQuery();
	const assigned = useAssignedMachineQuery();

	return useMemo(() => {
		return {
			machines: machinesQuery.data?.map((c) => ({ id: c.machineId, value: c.name })) ?? [],
			machinesLoaded: machinesQuery.loaded,
			assigned: assigned.data,
		};
	}, [machinesQuery.data, assigned.data]);
}

function useFeatureInfo() {
	const { useDisabledFeaturesQuery } = useRepQueries();
	const { data, loaded: disabledFeaturesLoaded } = useDisabledFeaturesQuery();

	return useMemo(() => {
		return {
			disabledFeatures: data,
			disabledFeaturesLoaded,
		};
	}, [data]);
}

function useManagerQuery<T extends ActivableFilterItem>(name: string, api: (...params) => Promise<T[]>) {
	const { rep } = useRepContext();
	const [items, loaded] = useFilterItemQuery<T>([name], api, rep.repId, rep.branchId);

	const filteredItems = useMemo(() => {
		if (!loaded || !items) return emptyArray;
		return rep.showInactiveManagers ? items : items.filter((m) => m.active);
	}, [rep.showInactiveManagers, items, loaded]);
	return [filteredItems, loaded] as [KeyedArray<T>, boolean];
}

interface IPersonalInfoList {
	managers: keyPair[];
	languages: keyPair[];
	repTypes: keyPair[];
	trainers: keyPair[];
}
const emptyArray = new KeyedArray<ActivableFilterItem>([], (k) => k.id);
